import React, { useState } from 'react';
import { BookOpen, Search, Plus, FileText, Lightbulb, Target, Palette, Layout, Users, Tag, Star } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { BrandGuidelinesUpload } from './BrandGuidelinesUpload';

export function KnowledgeBasePage({ userId }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All', icon: BookOpen },
    { id: 'design-principles', name: 'Design Principles', icon: Lightbulb },
    { id: 'brand-guidelines', name: 'Brand Guidelines', icon: Palette },
    { id: 'layout-best-practices', name: 'Layout & Composition', icon: Layout },
    { id: 'presentation-tips', name: 'Presentation Tips', icon: Target },
    { id: 'client-resources', name: 'Client Resources', icon: Users },
  ];

  const knowledgeItems = [
    {
      id: 1,
      title: 'Design Compass Framework',
      category: 'design-principles',
      type: 'Framework',
      description: 'A comprehensive framework for analyzing presentation design quality and providing actionable feedback.',
      content: 'The Design Compass Framework evaluates presentations across 4 key dimensions: Narrative Structure, Visual Hierarchy, Brand Consistency, and Audience Engagement.',
      tags: ['framework', 'analysis', 'quality'],
      featured: true,
      lastUpdated: '2024-07-28'
    },
    {
      id: 2,
      title: 'Color Psychology in Presentations',
      category: 'design-principles',
      type: 'Guide',
      description: 'Understanding how colors affect audience perception and engagement in business presentations.',
      content: 'Colors evoke emotions and can significantly impact how your message is received. Blue conveys trust and professionalism, while red creates urgency and excitement.',
      tags: ['color', 'psychology', 'engagement'],
      featured: false,
      lastUpdated: '2024-07-25'
    },
    {
      id: 3,
      title: 'Corporate Brand Standards',
      category: 'brand-guidelines',
      type: 'Guidelines',
      description: 'Standard guidelines for maintaining brand consistency across all presentation materials.',
      content: 'Proper logo usage, color specifications, typography guidelines, and spacing requirements for corporate presentations.',
      tags: ['branding', 'logo', 'guidelines'],
      featured: true,
      lastUpdated: '2024-07-27'
    },
    {
      id: 4,
      title: 'Slide Layout Best Practices',
      category: 'layout-best-practices',
      type: 'Best Practices',
      description: 'Proven techniques for creating clean, professional slide layouts that enhance readability.',
      content: 'Use the rule of thirds, maintain consistent margins, limit text per slide, and ensure proper contrast between text and background.',
      tags: ['layout', 'composition', 'readability'],
      featured: false,
      lastUpdated: '2024-07-26'
    },
    {
      id: 5,
      title: 'Executive Presentation Guidelines',
      category: 'presentation-tips',
      type: 'Tips',
      description: 'Essential guidelines for creating presentations that resonate with C-level executives.',
      content: 'Focus on high-level insights, use data storytelling, keep slides minimal, and always start with the key takeaway.',
      tags: ['executive', 'storytelling', 'strategy'],
      featured: true,
      lastUpdated: '2024-07-24'
    },
    {
      id: 6,
      title: 'Client Onboarding Checklist',
      category: 'client-resources',
      type: 'Checklist',
      description: 'Complete checklist for gathering client requirements and brand assets for presentation projects.',
      content: 'Brand guidelines, logo files, color codes, font preferences, presentation templates, previous presentations, and content outline.',
      tags: ['onboarding', 'checklist', 'client'],
      featured: false,
      lastUpdated: '2024-07-23'
    },
  ];

  const filteredItems = knowledgeItems.filter(item => {
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const matchesSearch = searchQuery === '' ||
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));

    return matchesCategory && matchesSearch;
  });

  const featuredItems = knowledgeItems.filter(item => item.featured);

  return (
    <div className="p-8 space-y-8 min-h-screen max-w-7xl mx-auto">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold text-foreground">Knowledge Base</h1>
        <p className="text-lg text-muted-foreground">Design resources, guidelines, and best practices</p>
      </div>

      {/* Search and Actions */}
      <Card className="bg-card/50 backdrop-blur-sm border-border/50">
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1 max-w-lg">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                placeholder="Search knowledge base..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 bg-input border-border focus:border-accent"
              />
            </div>
            <Button className="bg-gradient-to-r from-accent to-accent/90 text-accent-foreground">
              <Plus className="w-4 h-4 mr-2" />
              Add Resource
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Categories Sidebar */}
        <div className="space-y-6">
          <Card className="bg-card/50 backdrop-blur-sm border-border/50">
            <CardHeader>
              <CardTitle className="text-lg">Categories</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {categories.map((category) => {
                const Icon = category.icon;
                const isActive = selectedCategory === category.id;

                return (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all text-left ${
                      isActive
                        ? 'bg-accent/10 text-accent border border-accent/20'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="font-medium">{category.name}</span>
                  </button>
                );
              })}
            </CardContent>
          </Card>

          {/* Featured Items */}
          <Card className="bg-gradient-to-br from-accent/5 to-accent/10 border-accent/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="w-5 h-5 text-accent" />
                Featured
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {featuredItems.slice(0, 3).map((item) => (
                <div key={item.id} className="p-3 bg-background/50 rounded-lg">
                  <h4 className="font-semibold text-foreground text-sm mb-1">{item.title}</h4>
                  <p className="text-xs text-muted-foreground">{item.type}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3 space-y-6">
          {/* Brand Guidelines Upload Section - Show when brand-guidelines category is selected */}
          {selectedCategory === 'brand-guidelines' && (
            <BrandGuidelinesUpload userId={userId} />
          )}

          {/* Results Count */}
          <div className="flex items-center justify-between">
            <p className="text-muted-foreground">
              Showing {filteredItems.length} of {knowledgeItems.length} resources
            </p>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Sort by:</span>
              <select className="text-sm bg-background border border-border rounded-md px-3 py-1">
                <option>Most Recent</option>
                <option>Most Relevant</option>
                <option>Alphabetical</option>
              </select>
            </div>
          </div>

          {/* Knowledge Items */}
          <div className="space-y-4">
            {filteredItems.map((item) => (
              <Card key={item.id} className="bg-card/50 backdrop-blur-sm border-border/50 hover:border-accent/30 transition-all cursor-pointer">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-foreground">{item.title}</h3>
                        {item.featured && (
                          <Star className="w-4 h-4 text-accent fill-current" />
                        )}
                      </div>
                      <p className="text-muted-foreground mb-3">{item.description}</p>
                      <p className="text-sm text-foreground leading-relaxed mb-4">{item.content}</p>
                    </div>
                    <Badge variant="secondary" className="ml-4">
                      <FileText className="w-3 h-3 mr-1" />
                      {item.type}
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {item.tags.map((tag) => (
                        <Badge key={tag} variant="outline" className="text-xs">
                          <Tag className="w-3 h-3 mr-1" />
                          {tag}
                        </Badge>
                      ))}
                    </div>
                    <span className="text-sm text-muted-foreground">
                      Updated {new Date(item.lastUpdated).toLocaleDateString()}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredItems.length === 0 && (
            <Card className="bg-card/50 backdrop-blur-sm border-border/50">
              <CardContent className="text-center py-16">
                <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-accent/10 to-accent/5 flex items-center justify-center">
                  <Search className="w-8 h-8 text-accent" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-3">No results found</h3>
                <p className="text-lg text-muted-foreground mb-8 max-w-md mx-auto">
                  Try adjusting your search terms or browse different categories.
                </p>
                <Button
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedCategory('all');
                  }}
                  variant="outline"
                >
                  Clear Filters
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}